<template>
  <main class="landing-page">
    <!-- 1. Hero 섹션 -->
    <section
      class="landing-section landing-hero"
      :style="{ backgroundImage: 'url(' + heroBg + ')' }"
      aria-label="ICI Korea 소개"
    >
      <div class="hero-grid">
        <div class="hero-content reveal-up" data-reveal>
          <h1 class="hero-title">
            ICI Korea Recognizing Competency World-Wide
          </h1>
          <p class="hero-subtitle">
            내부통제 전문성 · 국제 내부통제 기준 충족<br />
            <span style="font-weight: 700;">ICI Korea</span>에서 시작하십시오.
          </p>
          <p class="hero-body">
            조직 내부통제 · 거버넌스 자가진단 · 평가 · 인증까지 원스톱 제공<br/>
            국제 유일의 국제공인 내부통제 자격 CICS/CICP
          </p>
          <div class="hero-cta-group">
            <button class="hero-cta primary" @click="go('/iiccmain/selfassessment/surveypublic')">
               ✚ 거버넌스 자가진단 리포트
            </button>
            <button class="hero-cta secondary" @click="go({ path: '/certificationmain', query: { tab: 'cics' } })">
               ✚ CICS &amp; CICP 자격증 안내
            </button>
          </div>
        </div>

        <div class="hero-logo-wrap reveal-fade" data-reveal>
          <img :src="heroLogo" alt="ICI Korea 로고" class="hero-logo" />
        </div>
      </div>
    </section>

    <!-- 2. 내부통제가 중요한 이유 -->
    <section
      id="section-why"
      class="landing-section landing-why"
      aria-label="내부통제가 중요한 이유"
    >
      <div class="section-grid section-grid--why">
        <!-- 상단 타이틀 + 설명 -->
        <header class="why-header reveal-up" data-reveal>
          <h2 class="section-title1">내부통제가 중요한 이유</h2>
          <p class="why-lead">
            내부통제는 이제 단순히 회계나 재무의 문제가 아니라, 기업과 공공기관의
            지속가능한 경영과 신뢰를 지탱하는 핵심 기반이자 문화입니다.<br/> 그러나
            <strong> 부실 회계, 정보 유출, 비윤리적 의사결정</strong> 등 반복되는 사례는
            내부통제의 중요성을 다시 일깨우고 있습니다.
          </p>
        </header>

        <!-- 가운데 다이어그램(이미지) -->
        <figure class="why-diagram reveal-right" data-reveal aria-label="내부통제 중요성 흐름 다이어그램">
          <img
            :src="triangleImg"
            alt="ESG · 지배구조 평가 강화, 내부통제 수요 증가, 리스크 감소 및 경영 투명성 제고"
            class="why-diagram__img"
            loading="lazy"
          />
        </figure>

        <!-- 하단 설명 -->
        <div class="why-body reveal-up" data-reveal>
          <p>
            특히 ESG 및 지배구조 평가 기준이 강화되고, 공공기관·금융기관·민간기업 전반에 걸쳐
            내부통제에 대한 요구 수준이 높아지면서, 국제 기준에 부합하는 내부통제 체계 구축은
            리스크를 최소화하고 조직의 신뢰를 제고하는 핵심 요소로 인식되고 있습니다.
          </p>
        </div>

        <!-- 인용문 -->
        <blockquote class="why-quote reveal-up" data-reveal>
          <span class="quote-mark quote-mark--left" aria-hidden="true">“</span>
          <p class="why-quote__text">
            내부통제는 더 이상 선택이 아닌 필수이며, 조직 전반에 내재화되어야 할 경영의 기본 원칙입니다.
          </p>
          <span class="quote-mark quote-mark--right" aria-hidden="true">”</span>
        </blockquote>
      </div>
    </section>

    <!-- 3. 왜 한국내부통제협회(ICI Korea)인가? -->
    <section
      id="section-ici"
      class="landing-section landing-ici"
      aria-label="왜 ICI Korea 인가"
    >
      <div class="section-grid section-grid--ici">
        <!-- 상단 타이틀/소개 -->
        <header class="ici-header reveal-up" data-reveal>
          <h2 class="section-title2">왜 한국내부통제협회(ICI Korea) 인가?</h2>

          <p class="section-ici-intro">
            국제내부통제협회 (ICI, Internal Control Institute)의 공식 한국지부로,<br />
            국제 기준 기반 내부통제·거버넌스 진단, 교육, 자격 인증을 제공하는
            <span class="section-ici-intro__highlight">국내 유일 공식 기관</span>입니다.
          </p>
        </header>

        <!-- 2x2 카드 영역 -->
        <div class="ici-cards">
          <article class="ici-card reveal-up" data-reveal>
            <div class="ici-quote" aria-hidden="true">“</div>
            <div class="ici-card-body">
              <p class="ici-card-text">
                국제 기준 : COSO 프레임워크,<br />
                INTOSAI GOV 9100 기반
              </p>
            </div>
          </article>

          <article class="ici-card reveal-up" data-reveal>
            <div class="ici-quote" aria-hidden="true">“</div>
            <div class="ici-card-body">
              <p class="ici-card-text">
                서비스 범위 : 공공기관·금융<br />
                기관·민간기업 대상 내부통제<br />
                진단·평가·인증
              </p>
            </div>
          </article>

          <article class="ici-card reveal-up" data-reveal>
            <div class="ici-quote" aria-hidden="true">“</div>
            <div class="ici-card-body">
              <p class="ici-card-text">
                역할 : 내부통제 리스크 관리<br />
                역량·투명성 제고를 위한 전문<br />
                서비스 제공
              </p>
            </div>
          </article>

          <article class="ici-card reveal-up" data-reveal>
            <div class="ici-quote" aria-hidden="true">“</div>
            <div class="ici-card-body">
              <p class="ici-card-text">
                자격 운영 : CICS·CICP 국제<br />
                공인 자격 운영으로 공공·민간<br />
                내부통제 성숙도 향상 지원
              </p>
            </div>
          </article>
        </div>

        <!-- 하단 문장 -->
        <p class="ici-footer reveal-up" data-reveal>
          내부통제 체계 구축, 중장기 전략 수립, 전사 리스크 관리, 전문가 교육 및 국제 전문가자격 과정(CICS,
          CICP) 등을 통해 조직의 투명경영과 리스크 관리 수준 향상을 지원하고 있습니다.
        </p>
      </div>
    </section>

    <!-- 4. 거버넌스 자가진단 시스템 -->
    <section
      id="section-governance"
      class="landing-section landing-governance"
      aria-label="거버넌스 자가진단 시스템"
    >
      <div class="section-grid section-grid--full gov-wrap">

        <!-- 타이틀 -->
        <header class="gov-header reveal-up" data-reveal>
          <h2 class="section-title3"> ESG 거버넌스 자가진단 도구</h2>

          <!-- 요청: 헤더 아래 2문장 배치 -->
          <p class="gov-sub">
            국제 기준에 따라 조직의 지배구조 수준을 진단하는 전문 자가진단
            도구
          </p>

          <p class="gov-lead">
            자가진단도구는 조직의 지배구조(Corporate Governance) 및 내부통제 운영 수준을 국제 기준에 따라 객관적으로 평가할 수 있도록 설계된 전문 진단 시스템입니다.
          </p>
        </header>

        <!-- 상단 2컬럼: 좌 이미지 + 우 설명문단(기존 내용 그대로) -->
        <div class="gov-top">
          <figure class="gov-visual reveal-right" data-reveal>
            <img
              :src="triangleImg2"
              alt="Corporate Governance 진단 구조"
              class="gov-visual__img"
              loading="lazy"
            />
          </figure>

          <div class="gov-copy reveal-up" data-reveal>
            <p>
              내부감사인, 외부감사인, 감사위원회, 이사회, 최고경영진 등
              조직의 핵심 지배구조 기능 5개 영역을 대상으로, 각 기능별
              역할과 운영 수준을 스스로 점검하고 개선 방향을 도출할 수
              있도록 구성되어 있습니다.
            </p>

            <p>
              각 영역은 역할과 권한, 독립성, 윤리, 성과, 통제 수준, 보고
              체계 등 국제 기준에 기반한 세부 평가 항목으로 구성되어 있어, 조직의 지배구조와 내부통제 운영 상태를 체계적으로 확
              인할 수 있습니다.
            </p>
          </div>
        </div>

        <!-- li 3개(문구 그대로) -->
        <ul class="gov-desc-list reveal-up" data-reveal>
          <li>
            국제 기준에 따라 이사회·감사위원회·내부감사인·외부감사인·최고경영진 등
            <span class="hl-strong">핵심 지배구조 기능의 운영 수준</span>을 객관적으로 평가
          </li>
          <li>
            역할·권한·독립성·윤리·보고체계 등 주요 요소를 정밀 점검
          </li>
          <li>
            <span class="hl-strong">총 245개 세부 체크리스트</span> 기반 심층 진단으로
            지배구조 성숙도 관리
          </li>
        </ul>

        <!-- 결과 문장(그대로) -->
        <p class="gov-result reveal-up" data-reveal>
          진단 결과는 내부통제 고도화, 인증, 외부 감사 대응의
          <span class="section-ici-intro__highlight2">핵심 기초자료</span>로 활용 가능합니다.
        </p>

        <!-- 아이콘 4개: 아이콘 옆에 문구(요청 반영) -->
        <div class="icons-grid icons-grid--gov">
          <button
            v-for="icon in icons"
            :key="icon.label"
            type="button"
            class="icon-card icon-card--gov icon-card--inline reveal-up"
            data-reveal
          >
            <div class="icon-image-wrap icon-image-wrap--inline">
              <img :src="icon.src" :alt="icon.label" />
            </div>
            <p class="icon-label icon-label--inline">{{ icon.label }}</p>
          </button>
        </div>


        <!-- 설명 + 이미지 슬라이더 그리드 -->
        <div class="gov-grid">
          <div class="gov-slider reveal-right" data-reveal>
            <div class="slider-frame">
              <transition name="fade-slide" mode="out-in">
                <img
                  :key="currentIndex"
                  :src="currentCarouselImage"
                  alt="거버넌스 자가진단 화면"
                  class="slider-image"
                />
              </transition>
            </div>
            <div class="slider-controls">
              <button
                type="button"
                class="slider-btn"
                @click="prevImage"
                aria-label="이전 이미지"
              >
                ‹
              </button>
              <!-- <div class="slider-dots">
                <button
                  v-for="(img, idx) in carouselImages"
                  :key="img"
                  type="button"
                  class="slider-dot"
                  :class="{ 'slider-dot--active': idx === currentIndex }"
                  @click="goToImage(idx)"
                  :aria-label="`이미지 ${idx + 1} 보기`"
                ></button>
              </div> -->
              <button
                type="button"
                class="slider-btn"
                @click="nextImage"
                aria-label="다음 이미지"
              >
                ›
              </button>
            </div>
            <p class="slider-hint">
              좌·우 버튼을 눌러 화면 예시를 살펴보세요.
            </p>
          </div>
          <div class="g2btn-wrap">
            <button class="hero-cta2 primary2" @click="go('/iiccmain/selfassessment/surveypublic')">
                ✚ 거버넌스 자가진단 리포트
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- 5. CICS & CICP 국제공인 내부통제 자격증 -->
    <section
      id="section-cert"
      class="landing-section landing-cert"
      aria-label="CICS & CICP 국제공인 내부통제 자격증"
    >
      <div class="section-grid section-grid--full cert-wrap">
        <!-- Header -->
        <header class="cert-header reveal-up" data-reveal>
          <h2 class="section-title2">CICS &amp; CICP 국제공인 내부통제 자격증</h2>
          <p class="cert-intro">
            CICS &amp; CICP 국제내부통제협회(ICI)가 인증하는
            <span class="section-ici-intro__highlight">글로벌 내부통제 전문자격</span>으로, 내부통제
            실무자부터 최고 책임자까지 조직의 통제 역량과 투명경영 수준을
            <span class="section-ici-intro__highlight"
              >국제 기준(COSO·INTOSAI)에 맞춰 검증하는 국내 유일의 국제공인 자격</span
            >입니다.
          </p>
        </header>

        <!-- 가치 3개 (가운데 라인 느낌) -->
        <div class="value-grid value-grid--cert">
          <article class="value-card value-card--cert reveal-up" data-reveal>
            <div class="value-icon">👤</div>
            <h3 class="value-title">개인적 가치</h3>
            <ul class="gov-list2">
              <li>국제적으로 인정받는 전문 자격</li>
              <li>커리어 경쟁력 강화 및 차별화</li>
              <li>내부통제 전문성 체계적 확보</li>
            </ul>
          </article>

          <article class="value-card value-card--cert reveal-up" data-reveal>
            <div class="value-icon">🏢</div>
            <h3 class="value-title">조직적 가치</h3>
            <ul class="gov-list2">
              <li>감사원 · 기재부 등 외부 평가 대응력 제고</li>
              <li>ESG·투명경영 기반 구축을 통한 지속가능 경영 선도</li>
              <li>리스크 관리 고도화 및 내부통제 문화 확산</li>
            </ul>
          </article>

          <article class="value-card value-card--cert reveal-up" data-reveal>
            <div class="value-icon">🌍</div>
            <h3 class="value-title">글로벌 가치</h3>
            <ul class="gov-list2">
              <li>ICI(국제내부통제협회) 글로벌 인증</li>
              <li>국제적 통용 표준에 따른 신뢰도 확보</li>
              <li>글로벌 전문가 네트워크 연계</li>
            </ul>
          </article>
        </div>

        <!-- 자격증 2개 카드 (이미지 사용) -->
        <div class="cert-grid">
          <!-- CICS -->
          <article class="cert-card cert-card--cics reveal-up" data-reveal>
            <header class="cert-card__head">
              <h3 class="cert-card__en">Certified Internal Control Specialist</h3>
              <h2 class="cert-card__ko">CICS 국제공인 내부통제사</h2>
            </header>

            <ul class="cert-check">
              <li>내부통제의 실행 및 평가 중심의 실무 전문가 양성</li>
              <li>내부통제 실행·평가, 리스크 관리, 프로세스 개선</li>
              <li>체계적인 전문성 함양</li>
            </ul>

            <figure class="cert-img">
              <img :src="triangleImg4" alt="CICS 자격증 예시 이미지" />
            </figure>
          </article>

          <!-- CICP -->
          <article class="cert-card cert-card--cicp reveal-up" data-reveal>
            <header class="cert-card__head">
              <h3 class="cert-card__en">Certified Internal Control Professional</h3>
              <h2 class="cert-card__ko">CICP 국제공인 전문내부통제사</h2>
            </header>

            <ul class="cert-check">
              <li>내부통제 관리(평가) 책임자에 특화</li>
              <li>내부통제 전략·정책 수립, 조직 문화 확산, 대외 감사 대응</li>
              <li>내부통제 리더의 표준</li>
            </ul>

            <figure class="cert-img">
              <img :src="triangleImg3" alt="CICP 자격증 예시 이미지" />
            </figure>
          </article>
        </div>

        <!-- CTA -->
        <div class="cert-cta reveal-up" data-reveal>
          <button
            class="hero-cta primary"
            @click="go({ path: '/certificationmain', query: { tab: 'cics' } })"
          >
            ✚ CICS &amp; CICP 자격증 안내
          </button>
        </div>
      </div>
    </section>

    <!-- Floating Top Button -->
    <button
      v-if="showScrollTop"
      type="button"
      class="scroll-top-btn"
      @click="scrollToTop"
      aria-label="페이지 상단으로 이동"
    >
      ↑
    </button>

    <!-- Footer -->
    <footer class="landing-footer">
      <div class="hero-logo-wrap2 reveal-fade" >
        <img :src="heroLogo" alt="ICI Korea 로고" class="hero-logo2" />
      </div>
      <p>
        한국내부통제협회 ICI Korea | 문의 : 070-4489-7447 | www.icikorea.kr
      </p>
    </footer>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

import heroBgSrc from '@/assets/landing/landing1.png'
import heroLogoSrc from '@/assets/landing/landing2.png'
import triangleImgSrc from '@/assets/landing/landing1-1.png'
import triangleImgSrc2 from '@/assets/landing/landing2-1.jpg'
import triangleImgSrc3 from '@/assets/landing/landing4-1.png'
import triangleImgSrc4 from '@/assets/landing/landing4-2.png'

import icon1 from '@/assets/landing/landingicon1.png'
import icon2 from '@/assets/landing/landingicon2.png'
import icon3 from '@/assets/landing/landingicon3.png'
import icon4 from '@/assets/landing/landingicon4.svg'

import picture1 from '@/assets/landing/picture1.png'
import picture2 from '@/assets/landing/picture2.png'
import picture3 from '@/assets/landing/picture3.png'
import picture4 from '@/assets/landing/picture4.png'
import { useRouter } from 'vue-router'

const heroBg = heroBgSrc
const heroLogo = heroLogoSrc
const triangleImg = triangleImgSrc
const triangleImg2 = triangleImgSrc2
const triangleImg3 = triangleImgSrc3
const triangleImg4 = triangleImgSrc4

const icons = [
  { src: icon1, label: '거버넌스 구조 진단' },
  { src: icon2, label: '거버넌스 설계 및 운영의 종합적 평가' },
  { src: icon3, label: '지속적 개선 및 리스크 관리 강화' },
  { src: icon4, label: 'ICI 국제 기준에 따른 거버넌스 등급 산정' },
]

const carouselImages = [picture1, picture2, picture3, picture4]
const router = useRouter()

const currentIndex = ref(0)
const showScrollTop = ref(false)
// const highlightedIcon = ref(null)

// ==== 내비게이션 ====
function go(to) {                 // 문자열/라우트 객체 모두 호환
  router.push(to)
}

let observer = null
let autoPlayTimer = null

const currentCarouselImage = computed(() => carouselImages[currentIndex.value])

const prevImage = () => {
  const lastIndex = carouselImages.length - 1
  currentIndex.value =
    currentIndex.value === 0 ? lastIndex : currentIndex.value - 1
  restartAutoPlay()
}

const nextImage = () => {
  const lastIndex = carouselImages.length - 1
  currentIndex.value =
    currentIndex.value === lastIndex ? 0 : currentIndex.value + 1
  restartAutoPlay()
}

const goToImage = (index) => {
  currentIndex.value = index
  restartAutoPlay()
}

const startAutoPlay = () => {
  stopAutoPlay()
  autoPlayTimer = setInterval(() => {
    nextImage()
  }, 5000)
}

const stopAutoPlay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
}

const restartAutoPlay = () => {
  startAutoPlay()
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// const setHighlightedIcon = (label) => {
//   highlightedIcon.value = label
// }

const handleScroll = () => {
  const y = window.scrollY || window.pageYOffset || 0
  showScrollTop.value = y > 400
}

onMounted(() => {
  // 스크롤 이벤트
  window.addEventListener('scroll', handleScroll, { passive: true })

  // IntersectionObserver
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.2 }
  )

  document
    .querySelectorAll('[data-reveal]')
    .forEach((el) => observer.observe(el))

  // 슬라이더 자동 재생
  startAutoPlay()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  if (observer) {
    observer.disconnect()
    observer = null
  }
  stopAutoPlay()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700&display=swap');

.landing-page {
  font-family: 'Noto Sans KR', sans-serif;
  color: #222;
  background-color: #f7f7f7;
}

/* 공통 섹션 */
.landing-section {
  padding: 4rem 1.5rem;
  /* text-wrap: pretty; */
  word-break: keep-all;
}

.section-grid {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  gap: 2.5rem;
}

.section-grid--full {
  grid-template-columns: minmax(0, 1fr);
}

.section-title1 {
  font-size: 1.825rem;
  font-weight: 700;
  margin-bottom: 0.85rem;
}

.section-title2 {
  font-size: 1.825rem;
  font-weight: 700;
  /* margin-bottom: 1.25rem; */
  margin: 0 0 1.25rem;
  color: #ffffff;
}

.section-title3 {
  font-size: 1.825rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #222;
}

.section-title {
  font-size: 1.825rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  color: #222;
}

/* p 태그 공통 */
p {
  font-size: 1.09rem;
  line-height: 1.7;
  margin-bottom: 0.75rem;
}

.section-header--center p {
  margin-left: auto;
  margin-right: auto;
}

/* Hero 섹션 */
.landing-hero {
  background-size: cover;
  background-position: center;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.landing-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(0, 0, 0, 0.7), rgba(0, 43, 89, 0.4));
}

.hero-grid {
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 2rem;
  align-items: flex-start;
  padding: 2rem 1.5rem;
}

.hero-content {
  max-width: 640px;
}

.hero-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.hero-subtitle {
  font-size: 2rem;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.hero-body {
  font-size: 1.2rem;
  margin-bottom: 2rem;
}

.hero-cta-group {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 0.75rem;
}

.hero-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 9px;
  font-size: 0.95rem;
  font-weight: 500;
  border: 1px solid #ffffff;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease, color 0.2s ease;
}

.hero-cta.primary {
  background-color: transparent;

  border-color: #ffffff;
  color: #ffffff;
}

.g2btn-wrap{
  display: flex;
  justify-content: center;
}

.hero-cta2 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 9px;
  font-size: 0.95rem;
  font-weight: 500;
  border: 1px solid #000000;
  text-decoration: none;
  cursor: pointer;
  background-color: #ffffff;
}

.hero-cta2:hover{
  /* transform: translateY(-1px); */
  opacity: 0.96;
  background-color: #2f2c86;
  color: #ffffff;
}

.hero-cta.primary2 {
  background-color: #ffffff;
  border-color: #ffffff;
  color: #ffffff;
}

.hero-cta.secondary {
  background-color: transparent;
  color: #ffffff;
}

.hero-cta:hover {
  transform: translateY(-1px);
  opacity: 0.96;
  background-color: #ffffff;
  color: #000000;
}

.hero-logo-wrap {
  display: flex;
  justify-content: flex-end;
}

.hero-logo-wrap2 {
  display: flex;
  justify-content: flex-end;
}
.hero-logo {
  width: 200px;
  height: auto;
}
.hero-logo2 {
  width: 80px;
  height: auto;
}

/* 내부통제 중요 섹션 */
.landing-why {
  background-color: #ffffff;
}

.section-grid--why {
  /* 상단-다이어그램-하단-인용문 순서 고정 */
  grid-template-columns: 1fr;
  align-items: start;
}

.why-header {
  display: grid;
  gap: 0.5rem;
}

.why-lead strong {
  font-weight: 700;
}

.why-lead{
  margin: 0 30px;
}

/* 다이어그램 */
.why-diagram {
  display: grid;
  place-items: center;
  margin: 0;
}

.why-diagram__img {
  width: min(780px, 100%);
  height: auto;
}

/* 하단 본문 */
.why-body {
  display: grid;
  margin: 0 30px;
}

/* 인용문 (큰 따옴표 스타일) */
.why-quote {
  position: relative;
  margin: 0;
  display: grid;
  grid-template-columns: auto max-content auto;
  column-gap: 0.5rem; /* 핵심 */
  justify-content: center;
  text-align: center;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
  /* 스크린샷 느낌: 아래쪽 공간 확보 */
  border-radius: 10px;
}

.why-quote__text {
  margin: 0;
  font-style: italic;
  font-weight: 500;
  font-size: 1.05rem;
  line-height: 1.6;
  color: #1f1f1f;
}

.quote-mark {
  font-size: 3rem;
  line-height: 1;
  font-weight: 700;
  color: #2f2c86; /* 이미지의 보라 계열 느낌 */
  transform: translateY(-2px);
}

.quote-mark--left {
  justify-self: start;
}

.quote-mark--right {
  justify-self: end;
}

/* ICI Korea 섹션 */
.landing-ici {
  background-color: #121555;
}

/* 소개 문단 */
.section-ici-intro {
  font-size: 1.25rem;
  line-height: 1.8;
  color: #ffffff;
  margin-top: 0.25rem;
  margin-bottom: 2.5rem;
}

.section-ici-intro__highlight {
  color: #37bfe3;
  font-weight: 700;
}

/* 카드 그리드: 2x2 + 큰 간격 (스크린샷 느낌) */
.ici-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 4.5rem;
  row-gap: 2.25rem;
  padding-top: 0.25rem;
}

/* 카드: 박스 없이 텍스트 덩어리로 */
.ici-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 0.9rem;
  align-items: start;
  color: #ffffff;
}

/* 큰 따옴표 */
.ici-quote {
  font-size: 2.6rem;
  line-height: 1;
  font-weight: 800;
  color: #ffffff;
  transform: translateY(-2px);
}

/* 카드 텍스트 */
.ici-card-text {
  font-size: 1.15rem; /* 스크린샷처럼 큼 */
  line-height: 1.9;
  font-weight: 600;
  color: #ffffff;
  text-align: left;
}

/* 하단 문장 */
.ici-footer {
  margin-top: 1.25rem;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.8;
}




/* 거버넌스 섹션 */
.gov-header {
  display: grid;
  gap: 0.55rem;
}

.gov-sub {
  font-size: 1.1rem;
  line-height: 1.75;
  color: #2d57b9;
  font-weight: 500;
  margin: 0 30px;
}

.gov-lead {
  font-size: 1.1rem;
  line-height: 1.75;
  color: #111827;
  /* max-width: 75ch; */
  margin: 0 30px;

}

/* 섹션 배경 유지 */
.landing-governance {
  background-color: #ffffff;
}

/* ===== 구조 핵심: 상단 2컬럼 ===== */
.gov-top {
  display: grid;
  grid-template-columns: minmax(0, 460px) minmax(0, 1fr);
  gap: 2.25rem;
  align-items: start;
}

/* 좌측 이미지 */
.gov-visual {
  margin: 0;
  display: grid;
  place-items: center;
}

.gov-visual__img {
  width: clamp(240px, 32vw, 340px);
  height: auto;
}

/* 우측 문단: 가독성 폭 제한(내용은 그대로, 폭만 제어) */
.gov-copy {
  display: grid;
  gap: 1rem;
  /* max-width: 62ch; */
}

.gov-copy p{
  font-size: 1.2rem;

}

/* ===== desc 리스트(·) ===== */
.gov-desc-list {
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.55rem;
}

.hl-strong {
  font-weight: 700;
  color: #37bfe3;
}

.gov-desc-list li {
  list-style: none;
  position: relative;
  padding-left: 0.9rem;
  font-size: 1.1rem;
  line-height: 1.75;
  color: #111827;
}

.gov-desc-list li::before {
  content: '·';
  position: absolute;
  left: 0;
  top: 0.1em;
  font-weight: 700;
}

/* 결과 문장 */
.gov-result {
  font-size: 1.2rem;
  line-height: 1.75;
}

.section-ici-intro__highlight2 {
  color: #37bfe3;
  font-weight: 700;
}

/* ===== 아이콘: 아이콘 위 / 텍스트 아래 ===== */
.icons-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;
}

.icon-card--gov {
  background: transparent;
  border: none;
  padding: 0.5rem;
  display: grid;
  justify-items: center;
  gap: 0.6rem;
}

.icon-image-wrap--gov {
  display: grid;
  place-items: center;
}

.icon-image-wrap--gov img {
  width: auto;
  height: 42px;
}

.icon-label--gov {
  text-align: center;
  font-size: 1.1rem;
  line-height: 1.4;
  margin: 0;
  color: #111827;
}

/* 아이콘: 옆 배치(아이콘 좌, 텍스트 우) */
.icon-card--inline {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  justify-items: start;
  gap: 0.85rem;
  padding: 0.9rem 0.75rem;
  border: none;
  background: transparent;
}

.icon-image-wrap--inline {
  display: grid;
  place-items: center;
}

.icon-image-wrap--inline img {
  width: auto;
  height: 42px;
}

.icon-label--inline {
  margin: 0;
  text-align: left;
  font-size: 0.95rem;
  line-height: 1.4;
  color: #111827;
  font-weight: 500;
}

/* 거버넌스 설명 + 슬라이더 */
.gov-grid {
  display: grid;
  grid-template-columns: minmax(1, 1.4fr) minmax(1, 1.3fr);
  gap: 2rem;
  align-items: center;
}

.gov-list {
  padding-left: 1.1rem;
}

.gov-list li {
  font-size: 1.25rem;
  line-height: 1.7;
  margin-bottom: 0.5rem;
  list-style: disc;
}

.gov-list2 {
  padding-left: 1.1rem;
  margin-top: 15px;
  color: #fff;
}

.gov-list2 li {
  font-size: 1rem;
  margin-bottom: 0.5rem;
  list-style: disc;
}

.gov-slider {
  display: grid;
  gap: 0.75rem;
  width: 600px;
}

.slider-frame {
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;   /* 권장: 반응형 + 높이 안정 */
  overflow: hidden;
}

.slider-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.slider-controls {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
}

.slider-btn {
  border: 1px solid #d0d4dc;
  border-radius: 999px;
  width: 2.2rem;
  height: 2.2rem;
  font-size: 1.3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  cursor: pointer;
  transition: background-color 0.18s ease, transform 0.18s ease,
    border-color 0.18s ease;
}

.slider-btn:hover {
  background-color: #f1f2f6;
  transform: translateY(-1px);
  border-color: #c3c7d0;
}

.slider-dots {
  display: flex;
  justify-content: center;
  gap: 0.4rem;
}

.slider-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  border: 0;
  background-color: #d0d4dc;
  cursor: pointer;
  transition: width 0.18s ease, background-color 0.18s ease,
    transform 0.18s ease;
}

.slider-dot--active {
  width: 0.9rem;
  background-color: #ffcc33;
  transform: translateY(-1px);
}

.slider-hint {
  font-size: 0.82rem;
  color: #6b7280;
}

/* 슬라이드 전환 애니메이션 */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.cert-header {
  display: grid;
  gap: 0.65rem;
}

.cert-intro {
  color: #ffffff;
  max-width: 92ch;
}

.section-ici-intro__highlight {
  color: #37bfe3;
  font-weight: 700;
}

/* ===== 상단 가치 3개: 가운데 라인 느낌 ===== */
.value-grid--cert {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.75rem;
  position: relative;
  padding-top: 0.5rem;
}

/* 각 카드 사이에 세로 라인(스크린샷 느낌) */
.value-card--cert {
  padding: 1.2rem 1.1rem;
  display: grid;
  gap: 0.65rem;
  position: relative;
}

/* 가운데/오른쪽 카드 왼쪽에 라인 */
.value-card--cert:not(:first-child)::before {
  content: '';
  position: absolute;
  left: -0.9rem;
  top: 0.35rem;
  bottom: 0.35rem;
  width: 1px;
  background: rgba(255, 255, 255, 0.28);
}

/* 라인 위/아래 점(스크린샷의 원형 포인트 느낌) */
.value-card--cert:not(:first-child)::after {
  content: '';
  position: absolute;
  left: -1.05rem;
  top: 0.25rem;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
}

.value-icon {
  font-size: 3rem;
  text-align: center;
}

.value-title {
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  color: #fff;
}

.gov-list2 {
  padding-left: 1.1rem;
  margin-top: 0.25rem;
  color: #fff;
}

.gov-list2 li {
  font-size: 0.95rem;
  margin-bottom: 0.45rem;
  list-style: disc;
}

/* 자격증 섹션 */
.landing-cert {
  background-color: #121555;
}

/* ===== 자격증 2개 카드 ===== */
.cert-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.75rem;
  margin-top: 0.25rem;
}

.cert-card {
  border-radius: 22px;
  padding: 1.5rem 1.5rem 1.25rem;
  display: grid;
  gap: 1rem;
  overflow: hidden;
}

/* 스크린샷처럼 좌/우 카드 배경 톤 다르게 */
.cert-card--cics {
  background: rgba(25, 74, 122, 0.92);
}
.cert-card--cicp {
  background: rgba(9, 84, 95, 0.92);
}

.cert-card__head {
  display: grid;
  gap: 0.25rem;
  text-align: center;
}

.cert-card__en {
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
}

.cert-card__ko {
  font-size: 1.7rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
}

.cert-check {
  margin: 0;
  padding: 0 1.75rem;
  display: grid;
  gap: 0.4rem;
}

.cert-check li {
  list-style: none;
  position: relative;
  padding-left: 1.35rem;
  color: #ffffff;
  font-size: 0.95rem;
  line-height: 1.55;
}

.cert-check li::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0.02rem;
  font-weight: 800;
  color: #ffd54d;
}

.cert-img {
  margin: 0;
  padding: 0.75rem;
  display: grid;
  place-items: center;
}

.cert-img img {
  width: 100%;
  max-width: 420px;
  height: auto;
  display: block;
}

/* CTA */
.cert-cta {
  text-align: center;
}

.hero-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 9px;
  font-size: 0.95rem;
  font-weight: 500;
  border: 1px solid #ffffff;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease, color 0.2s ease;
}

.hero-cta.primary {
  background-color: transparent;
  border-color: #ffffff;
  color: #ffffff;
}

.hero-cta:hover {
  transform: translateY(-1px);
  opacity: 0.96;
  background-color: #ffffff;
  color: #000000;
}

/* Floating Top Button */
.scroll-top-btn {
  position: fixed;
  right: 1.3rem;
  bottom: 1.5rem;
  width: 2.7rem;
  height: 2.7rem;
  border-radius: 999px;
  border: none;
  background-color: #111827;
  color: #f9fafb;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 50;
  transition: transform 0.18s ease, background-color 0.18s ease, opacity 0.18s ease;
  opacity: 0.95;
}

.scroll-top-btn:hover {
  transform: translateY(-2px);
  background-color: #0f172a;
}

/* Footer */
.landing-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  background-color: #111827;
  color: #e5e7eb;
  gap: 1rem;
}

/* reveal 애니메이션 (유지) */

[data-reveal] {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.reveal-up[data-reveal] {
  transform: translateY(20px);
}

.reveal-right[data-reveal] {
  transform: translateX(20px);
}


[data-reveal].is-visible {
  opacity: 1;
  transform: translate(0, 0);
}

/* 반응형: 모바일 중심 튜닝 */
@media (max-width: 960px) {
  .landing-section {
    padding: 3.25rem 1.25rem;
  }

  .hero-grid {
    grid-template-columns: minmax(0, 1fr);
    padding-inline: 1.25rem;
    padding-bottom: 4rem;
  }

  .hero-logo-wrap {
    justify-content: flex-start;
  }

  .hero-logo {
    width: 180px;
  }

  .why-quote {
    grid-template-columns: 1fr;
    gap: 0.4rem;
    padding: 1rem 1rem;
  }

  .quote-mark--left,
  .quote-mark--right {
    display: none; /* 모바일에서는 간결하게 */
  }

  .why-quote__text {
    font-size: 1rem;
  }

  .ici-cards {
    grid-template-columns: 1fr;
    row-gap: 2rem;
    column-gap: 0;
  }

  .ici-card-text {
    font-size: 1.05rem;
  }

  .ici-footer {
    font-size: 1rem;
  }

  .icons-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gov-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .gov-copy {
    max-width: 100%;
  }

  .icons-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hero-cta-group {
    grid-auto-flow: row;
    grid-auto-columns: 1fr;
    max-width: 340px;
  }
}

@media (max-width: 640px) {
  .landing-section {
    padding: 3rem 1.1rem;
  }
  .gov-slider{
    width: 400px;
  }

  .hero-title {
    font-size: 1.8rem;
    line-height: 1.2;
  }

  .section-title2 {
    font-size: 1.6rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }

  .hero-body {
    font-size: 0.9rem;
  }

  .cards-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .icons-grid {
    grid-template-columns: 1fr;
  }
  

  .value-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .scroll-top-btn {
    right: 1rem;
    bottom: 1.2rem;
    width: 2.5rem;
    height: 2.5rem;
  }
}

</style>
